import { useCallback, useSyncExternalStore } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  message: string;
  tone: 'info' | 'success' | 'error';
  /** Опциональное действие (например, «Отменить» promote). */
  action?: ToastAction;
}

type Listener = () => void;

let toasts: ToastItem[] = [];
let toastSeq = 0;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ToastItem[] {
  return toasts;
}

function pushToast(
  message: string,
  tone: ToastItem['tone'],
  durationMs: number,
  action?: ToastAction,
): void {
  toastSeq += 1;
  const item: ToastItem = {
    id: `toast-${toastSeq}`,
    message,
    tone,
    ...(action !== undefined ? { action } : {}),
  };
  toasts = [...toasts, item];
  emit();
  window.setTimeout(() => {
    toasts = toasts.filter((toast) => toast.id !== item.id);
    emit();
  }, durationMs);
}

export function showToast(message: string, tone: ToastItem['tone'] = 'info'): void {
  pushToast(message, tone, tone === 'error' ? 5000 : 3200);
}

/** Тост с кнопкой действия (undo и т.п.). Действие не закрывает тост автоматически. */
export function showToastWithAction(
  message: string,
  tone: ToastItem['tone'],
  action: ToastAction,
  durationMs = 6000,
): void {
  pushToast(message, tone, durationMs, action);
}

export function dismissToast(id: string): void {
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useToastActions(): {
  show: (message: string, tone?: ToastItem['tone']) => void;
  dismiss: (id: string) => void;
} {
  const show = useCallback((message: string, tone: ToastItem['tone'] = 'info') => {
    showToast(message, tone);
  }, []);
  const dismiss = useCallback((id: string) => {
    dismissToast(id);
  }, []);

  return { show, dismiss };
}
